import { useEffect, useRef } from "react"

interface UseConversationsSelectionResetOnFilterChangeParams {
  agentIds: string[]
  clusterFilterIds: string[] | null
  consumeSkipNextSelectionReset: () => boolean
  dateRange: unknown
  metadataFilters: unknown
  metricIds: string[]
  metricValueFilters: unknown
  searchQuery: string
  searchType: unknown
  setSelectedKeys: (keys: Set<string>) => void
  tagFilters: unknown
  projectIds: string[]
}

/**
 * Clears the row selection whenever any filter narrows or widens the result set, since
 * a selection made under one filter set may no longer refer to visible rows under
 * another. Skips the one filter change the visualizer makes on purpose alongside a
 * selection of its own (see useConversationsVisualizerSelection), and skips the initial
 * mount so a restored selection isn't wiped before the user does anything.
 *
 * searchType is tracked alongside searchQuery even though it never changes without the
 * visualizer also changing searchQuery or clusterFilterIds -- except the one case where a
 * user has already typed a conversation's exact id into an ALL_FIELDS search and then
 * clicks that same conversation's dot. That only flips searchType (SIMULATION_OUTPUT_ID),
 * a real change in which rows match, so it belongs in this effect's own dependencies
 * rather than relying on some other field to have changed too.
 */
export function useConversationsSelectionResetOnFilterChange({
  agentIds,
  clusterFilterIds,
  consumeSkipNextSelectionReset,
  dateRange,
  metadataFilters,
  metricIds,
  metricValueFilters,
  searchQuery,
  searchType,
  setSelectedKeys,
  tagFilters,
  projectIds,
}: UseConversationsSelectionResetOnFilterChangeParams) {
  const isInitialMount = useRef(true)

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false
      return
    }
    if (consumeSkipNextSelectionReset()) {
      return
    }
    setSelectedKeys(new Set())
  }, [
    dateRange,
    metricIds,
    agentIds,
    tagFilters,
    projectIds,
    metadataFilters,
    metricValueFilters,
    searchQuery,
    searchType,
    clusterFilterIds,
    consumeSkipNextSelectionReset,
    setSelectedKeys,
  ])
}
